import { useEffect, useState } from "react";
import { GOLD, NAVY, WHITE } from "../styles/palette";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../lib/supabase";
import { ScoopMark } from "./ScoopMark";
import { useIsMobile } from "../lib/useIsMobile";

// ════════════════════════════════════════════════════════════════════
//  ResetPasswordPage — PUBLIC, standalone "set a new password" page.
//  Rendered (by index.js) for the path /reset-password, OUTSIDE the authed
//  CRM (no login gate). Serves BOTH flows that issue a Supabase recovery
//  link:
//    • Forgot-password (operator clicks "Forgot password?" on the login
//      screen → request-password-reset emails a recovery link).
//    • Technician onboarding (Add Technician emails a "set your password"
//      invite — also a recovery link).
//
//  A Supabase recovery/invite link lands here with the session token in the
//  URL HASH: #access_token=...&refresh_token=...&type=recovery&expires_in=...
//  We parse the hash, use access_token as a Bearer token, and PUT the new
//  password to /auth/v1/user. No SDK; mirrors the app's existing auth fetch
//  idiom in src/lib/supabase.js.
//
//  NOTE: this page renders outside <App/>, so it does NOT receive the theme's
//  globalCss (no box-sizing reset, no ≤768px iOS input-zoom guard). Every
//  phone rule it needs is applied inline here.
// ════════════════════════════════════════════════════════════════════

function parseHash() {
  // The token arrives in the fragment (never sent to the server). Strip the
  // leading "#" and parse as query params.
  const raw = (window.location.hash || "").replace(/^#/, "");
  const params = new URLSearchParams(raw);
  return {
    access_token: params.get("access_token") || "",
    type: params.get("type") || "",
    error_description: params.get("error_description") || params.get("error") || "",
  };
}

// Best-effort: read the account email out of the (unverified) JWT payload so
// the form can carry a hidden autoComplete="username" field — that is what
// lets iOS/Safari keychain UPDATE the saved login instead of creating a
// second entry after the password changes. Never used for auth decisions.
function emailFromToken(token) {
  try {
    const part = token.split(".")[1] || "";
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json);
    return typeof payload.email === "string" ? payload.email : "";
  } catch (e) {
    return "";
  }
}

export function ResetPasswordPage() {
  const isMobile = useIsMobile();
  const [token, setToken]       = useState("");
  const [email, setEmail]       = useState("");
  const [linkError, setLinkError] = useState("");   // hash-level problem (invalid/expired/missing)
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [error, setError]       = useState("");     // inline form validation / submit error
  const [loading, setLoading]   = useState(false);
  const [done, setDone]         = useState(false);

  useEffect(() => {
    const { access_token, type: t, error_description } = parseHash();
    if (error_description) {
      setLinkError(error_description);
    } else if (!access_token || (t !== "recovery" && t !== "invite")) {
      setLinkError("invalid");
    } else {
      setToken(access_token);
      setEmail(emailFromToken(access_token));
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: "PUT",
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
      });
      if (r.ok) {
        // Clear the token from the URL so it can't be re-used / leaked via history.
        window.history.replaceState(null, "", window.location.pathname);
        setDone(true);
      } else {
        const data = await r.json().catch(() => ({}));
        const msg = data.error_description || data.msg || data.message || "";
        setError(
          msg ||
          "This reset link has expired or was already used. Please request a new one."
        );
      }
    } catch (err) {
      setError("Something went wrong. Please try again or request a new link.");
    }
    setLoading(false);
  }

  // Phone: 16px text is REQUIRED here — the global iOS auto-zoom guard does not
  // reach this page (see header note), and a 13px input makes Safari zoom in
  // on focus and never zoom back.
  const inputStyle = {
    width: "100%", padding: isMobile ? "12px 14px" : "10px 12px", border: "1px solid #dfe1e6", borderRadius: isMobile ? 8 : 6,
    fontSize: isMobile ? 16 : 13, fontFamily: "Inter,sans-serif", outline: "none", transition: ".12s", boxSizing: "border-box", minWidth: 0,
  };
  const labelStyle = { display: "block", fontSize: isMobile ? 13 : 12, fontWeight: 500, color: "#42526e", marginBottom: 5 };
  const primaryStyle = (busy) => ({
    display: "block", width: "100%", boxSizing: "border-box", textAlign: "center", textDecoration: "none",
    padding: isMobile ? "13px" : "11px", minHeight: isMobile ? 46 : undefined, background: busy ? "#c5cdd8" : NAVY, color: WHITE,
    border: "none", borderRadius: isMobile ? 8 : 6, fontSize: isMobile ? 15 : 14, fontWeight: 600, fontFamily: "Inter,sans-serif",
    cursor: busy ? "not-allowed" : "pointer", transition: ".12s", letterSpacing: "-.1px",
  });
  // Text link with a padded ≥36px tap area on phones; inline look on desktop.
  const linkStyle = {
    display: "inline-block", color: NAVY, fontWeight: 600, textDecoration: "none",
    fontSize: isMobile ? 14 : 12, padding: isMobile ? "8px 12px" : 0, minHeight: isMobile ? 36 : undefined, boxSizing: "border-box", lineHeight: 1.3,
  };
  const titleStyle = { fontFamily: "Inter,sans-serif", fontSize: 22, fontWeight: 700, color: "#1a1f36", letterSpacing: "-.3px" };

  return (
    <div style={{ minHeight: isMobile ? "100dvh" : "100vh", display: "flex", background: "#f5f6f8", boxSizing: "border-box", maxWidth: "100%", overflowX: "clip" }}>
      {/* Left panel — brand (mirrors AuthPage). Hidden on a phone so the form gets the full width. */}
      {!isMobile && (
      <div style={{ width: 420, background: NAVY, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 48, flexShrink: 0, boxSizing: "border-box" }}>
        <div style={{ marginBottom: 32, textAlign: "center" }}>
          <div style={{ marginBottom: 12 }}><ScoopMark size={52} /></div>
          <div style={{ fontFamily: "Inter,sans-serif", fontSize: 26, fontWeight: 800, color: WHITE, letterSpacing: "-.5px" }}>
            iScoop<span style={{ color: GOLD }}>CRM</span>
          </div>
          <div style={{ color: "rgba(255,255,255,.45)", fontSize: 13, marginTop: 6 }}>Scoopers on Doody</div>
        </div>
        <div style={{ color: "rgba(255,255,255,.2)", fontSize: 11, textAlign: "center" }}>
          iScoopCRM v7.0 · Built for your business
        </div>
      </div>
      )}

      {/* Right panel — content. On a phone it pins to the top (a centred form
          jumps when the keyboard opens) and respects the notch/home-bar. */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "center", boxSizing: "border-box",
        padding: isMobile ? "calc(24px + env(safe-area-inset-top)) 16px calc(32px + env(safe-area-inset-bottom))" : 40 }}>
        <div style={{ width: "100%", maxWidth: 400, minWidth: 0 }}>

          {/* Phone brand header — the navy panel is hidden on phones. */}
          {isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
              <ScoopMark size={36} />
              <div>
                <div style={{ fontFamily: "Inter,sans-serif", fontSize: 20, fontWeight: 800, color: NAVY, letterSpacing: "-.4px", lineHeight: 1.1 }}>
                  iScoop<span style={{ color: GOLD }}>CRM</span>
                </div>
                <div style={{ fontSize: 12, color: "#8993a4", marginTop: 2 }}>Scoopers on Doody</div>
              </div>
            </div>
          )}

          {/* Invalid / expired link */}
          {linkError && (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={titleStyle}>Link expired</div>
              </div>
              <div style={{ background: "#ffebe6", border: "1px solid #ffbdad", borderRadius: 6, padding: "10px 14px", fontSize: 13, color: "#bf2600", marginBottom: 20 }}>
                ⚠️ This reset link is invalid or has expired — request a new one.
              </div>
              <a href="/" style={primaryStyle(false)}>
                Back to sign in
              </a>
            </>
          )}

          {/* Success */}
          {!linkError && done && (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={titleStyle}>Password updated</div>
              </div>
              <div style={{ background: "#e3fcef", border: "1px solid #abf5d1", borderRadius: 6, padding: "10px 14px", fontSize: 13, color: "#006644", marginBottom: 20 }}>
                ✅ Password updated — you can now sign in.
              </div>
              <a href="/" style={primaryStyle(false)}>
                Go to sign in →
              </a>
            </>
          )}

          {/* Set-password form */}
          {!linkError && !done && (
            <>
              <div style={{ marginBottom: isMobile ? 20 : 24 }}>
                <div style={titleStyle}>Set a new password</div>
                <div style={{ fontSize: 13, color: "#8993a4", marginTop: 4 }}>
                  Choose a new password for your iScoopCRM account.
                </div>
              </div>

              {error && <div style={{ background: "#ffebe6", border: "1px solid #ffbdad", borderRadius: 6, padding: "10px 14px", fontSize: 13, color: "#bf2600", marginBottom: 16 }}>⚠️ {error}</div>}

              <form onSubmit={handleSubmit}>
                {/* Visually-hidden username so password managers update the
                    existing keychain entry (display:none is ignored by them). */}
                {email && (
                  <input type="email" name="username" autoComplete="username" value={email} readOnly tabIndex={-1} aria-hidden="true"
                    style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none", left: -9999 }} />
                )}
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>New password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                    placeholder="Min 8 characters" autoComplete="new-password" style={inputStyle}
                    onFocus={e => e.target.style.borderColor = NAVY}
                    onBlur={e => e.target.style.borderColor = "#dfe1e6"} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Confirm new password</label>
                  <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required
                    placeholder="••••••••" autoComplete="new-password" style={inputStyle}
                    onFocus={e => e.target.style.borderColor = NAVY}
                    onBlur={e => e.target.style.borderColor = "#dfe1e6"} />
                </div>
                <button type="submit" disabled={loading} style={primaryStyle(loading)}>
                  {loading ? "Updating..." : "Update password"}
                </button>
              </form>

              <div style={{ marginTop: isMobile ? 16 : 20, textAlign: "center", fontSize: isMobile ? 14 : 12, color: "#8993a4" }}>
                <a href="/" style={linkStyle}>Back to sign in</a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
